import { MANAGE_ROLES, ManageMeI } from "@/api/routes/manage";
import { PrimaryNavigationItemCollapsableWithIconI } from "@/components/navigation/primary/interfaces";

/**
 * An Admin sees the items that list "admin" (Admin covers every page, so an
 * item meant for Admins lists it); anyone else needs one of the listed roles
 * among their grants. Without `roles` the item is for everyone.
 */
const isAllowed = (
  { roles, feature }: PrimaryNavigationItemCollapsableWithIconI,
  me: ManageMeI | null
): boolean => {
  // A phase-2 page shows only while its switch is on (fails closed).
  if (feature && me?.features?.[feature] !== true) {
    return false;
  }

  if (!roles) {
    return true;
  }

  if (!me) {
    return false;
  }

  if (me.is_admin) {
    return roles.includes(MANAGE_ROLES.ADMIN);
  }

  return roles.some((role) => me.roles.some((grant) => grant.role === role));
};

/**
 * Keeps the items the user's roles allow (C14-F1-D33) and whose phase-2
 * feature is switched on; a group whose children
 * all drop out drops too. Hiding is UX only: the server checks every route.
 */
export const filterNavItemsByRoles = (
  items: Array<PrimaryNavigationItemCollapsableWithIconI>,
  me: ManageMeI | null
): Array<PrimaryNavigationItemCollapsableWithIconI> =>
  items.reduce<Array<PrimaryNavigationItemCollapsableWithIconI>>(
    (visible, item) => {
      if (!isAllowed(item, me)) {
        return visible;
      }

      if (!item.children) {
        return [...visible, item];
      }

      const children = filterNavItemsByRoles(item.children, me);

      return children.length > 0
        ? [...visible, { ...item, children }]
        : visible;
    },
    []
  );
